import { CommonModule, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AlertModule } from '@epicuro-next/ataraxia/alert';
import { ClickOutsideModule } from '@epicuro-next/ataraxia/click-outside';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { dynamicElementsTextKey } from '@epicuro-next/constants/dynamic-elements';
import { AbstractDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { DataProvidersResolver } from '@epicuro-next/data-providers/providers-registry';
import { DontTreeShake } from '@epicuro-next/elements/core';
import {
  IDynamicBindableValue,
  IDynamicItemValue,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { DynamicComponentRegister } from '@epicuro-next/platform/dynamic-components-registrator';
import { isNonNullish, isNullish, isPrimitive } from '@epicuro-next/utilities';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { NumberOrNoopPipe } from './number-or-noop.pipe';

const MAX_SAFE_INTEGER = Number.MAX_SAFE_INTEGER;

const BLACKLISTED_CHARACTERS: Record<string, string[]> = {
  number: ['e', '+', '-'],
};

@DynamicComponentRegister(dynamicElementsTextKey)
@Component({
  selector: 'epc-dynamic-elements-editable-text',
  templateUrl: './dynamic-elements-editable-text.component.html',
  styleUrls: ['./dynamic-elements-editable-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    AlertModule,
    ReactiveFormsModule,
    IconsModule,
    ClickOutsideModule,
    NumberOrNoopPipe,
  ],
  providers: [DecimalPipe],
})
export class DynamicElementsEditableTextComponent implements OnInit, OnDestroy {
  constructor(private dataProvidersResolver: DataProvidersResolver) {}

  @Input()
  public set isEditAllowed(value: IDynamicItemValue) {
    if (isPrimitive(value) || isNullish(value)) {
      this.isEditAllowed$.next(value as boolean);
      return;
    }

    const { property, provider } = value as IDynamicBindableValue;

    const service = this.dataProvidersResolver.resolve(provider, this.injector);

    if (!service) {
      return;
    }

    service
      .read<boolean>(property)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isEditAllowed$.next(result);
      });
  }

  @Input()
  public set maxCharacters(value: number) {
    this.maxCharacters$$.next(value);
  }

  @Input()
  public set isRequired(value: boolean) {
    this.isRequired$.next(value);
  }

  @Input()
  public set isMultiline(value: boolean) {
    this.isMultiline$.next(value);
  }

  @Input()
  public set placeholder(value: string) {
    this.placeholder$.next(value);
  }

  @Input()
  public useBlur = false;

  @Input()
  public set text(textValue: IDynamicItemValue) {
    if (isPrimitive(textValue) || isNullish(textValue)) {
      this.textValue$$.next(textValue as string);
      return;
    }

    const { property, provider } = textValue as IDynamicBindableValue;

    if (!this.service) {
      this.service = this.dataProvidersResolver.resolve(
        provider,
        this.injector,
      );
    }

    if (!this.service) {
      return;
    }

    this.service
      .read<string>(property)
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.textValue$$.next(value);
      });
    this.propertyName = property as string | null;
  }
  private cdr = inject(ChangeDetectorRef);
  private readonly injector = inject(Injector);
  private textValue$$ = new BehaviorSubject<string | null>(null);
  public readonly textValue$ = this.textValue$$.pipe(
    filter(isNonNullish),
    shareReplay({ refCount: true, bufferSize: 1 }),
  );

  private maxCharacters$$ = new BehaviorSubject<number>(MAX_SAFE_INTEGER);
  public textControl = new FormControl();
  public isEditAllowed$ = new BehaviorSubject<boolean>(false);
  public isMultiline$ = new BehaviorSubject<boolean>(false);
  public isEditInProgress$ = new BehaviorSubject<boolean>(false);
  public placeholder$ = new BehaviorSubject<string>('');
  public isRequired$ = new BehaviorSubject<boolean>(false);

  public maxCharacters$ = this.maxCharacters$$
    .asObservable()
    .pipe(filter((v) => v !== MAX_SAFE_INTEGER));
  private service: AbstractDataProvider | null = null;
  private propertyName: string | null = null;
  private destroy$ = new Subject<void>();

  public showWarningError$ = combineLatest([
    this.textControl.valueChanges.pipe(map((value) => value?.length ?? 0)),
    this.maxCharacters$,
  ]).pipe(
    map(([valueLength, maxCharacters]) => valueLength >= maxCharacters),
    distinctUntilChanged(),
  );

  @Input() public numberFormat = '1.2-2';
  @Input() public actionBtnClass = 'btn-light';

  @Input()
  public inputType = 'text';

  @Input()
  public saveOnClickOutside = false;

  @Output() public submitText = new EventEmitter<string>();

  @ViewChild('inputControl', { static: false })
  public inputControl: ElementRef | null = null;

  @ViewChild('textareaControl', { static: false })
  public textareaControl: ElementRef | null = null;

  @Output()
  public isFocused = new EventEmitter<boolean>();

  @HostListener('click', ['$event'])
  public click(event: MouseEvent) {
    event.stopPropagation();
  }

  public ngOnInit() {
    this.textValue$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.textControl.setValue(value, { emitEvent: true });
      this.cdr.markForCheck();
    });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public handleInputFocusIn(event: FocusEvent) {
    event.stopPropagation();
    if (this.inputType === 'number') {
      (event.target as HTMLInputElement).select();
    }
    this.isEditInProgress$.next(true);
    this.isFocused.next(true);
  }

  public cancel() {
    this.isEditInProgress$.next(false);
    if (this.textControl.dirty) {
      this.textValue$
        .pipe(
          take(1),
          tap((value) => {
            this.textControl.reset(value, { emitEvent: true });
          }),
        )
        .subscribe();
    }
  }

  public submit() {
    this.isEditInProgress$.next(false);
    if (this.textControl.valid) {
      this.submitText.emit(this.textControl.value);
      if (this.service) {
        this.service.write(this.textControl.value, this.propertyName as string);
      }
      this.textControl.markAsPristine();
    }
  }

  public clickOutsideHandler() {
    if (
      document.activeElement === this.inputControl?.nativeElement ||
      document.activeElement === this.textareaControl?.nativeElement
    ) {
      return;
    }

    if (
      this.saveOnClickOutside &&
      this.isEditInProgress$.value &&
      this.textControl.dirty
    ) {
      this.submit();
    } else {
      this.cancel();
    }
  }

  public isAllowedCharacter(event: KeyboardEvent) {
    const blacklist = BLACKLISTED_CHARACTERS[this.inputType];

    if (!blacklist) {
      return;
    }

    if (blacklist.includes(event.key)) {
      event.stopPropagation();
      event.preventDefault();
    }
  }

  public blurHandler() {
    if (this.useBlur) {
      this.clickOutsideHandler();
    }
  }
}

DontTreeShake(DynamicElementsEditableTextComponent);
